import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { BrazilDateToUTCEnd, BrazilDateToUTCStart } from '../utils/dateUtils';

export const DataCacheContext = createContext(null);

/**
 * Vendas por página do /sellout. 500 mantém a resposta leve sem transformar um
 * range de 90 dias numa rajada de centenas de requisições. Teto do backend:
 * 2.000 (SELLOUT_MAX_PAGE).
 */
const SELLOUT_PAGE_SIZE = 500;
/**
 * Teto de páginas por busca (50 mil vendas). Existe para o dia em que o
 * backend disser `has_more` para sempre: laço infinito de requisição derruba a
 * aba, e parar com erro é melhor que travar em silêncio.
 */
const SELLOUT_MAX_PAGES = 100;

const normalizeRange = (fromDate, toDate) => ({
    from: BrazilDateToUTCStart(fromDate),
    to: BrazilDateToUTCEnd(toDate),
});

const rangeCovers = (cached, requested) => {
    if (!cached?.from || !cached?.to) {
        return false;
    }
    const cachedFrom = new Date(cached.from);
    const cachedTo = new Date(cached.to);
    const reqFrom = new Date(requested.from);
    const reqTo = new Date(requested.to);

    return reqFrom >= cachedFrom && reqTo <= cachedTo;
};

export const DataCacheProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();

    const [orders, setOrders] = useState([]);
    const [ordersRange, setOrdersRange] = useState(null);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState(null);

    const inFlightKey = useRef(null);
    const inFlightPromise = useRef(null);
    // Refs para acessar valores atuais sem criar dependencias
    const ordersRef = useRef([]);
    const ordersRangeRef = useRef(null);

    // Nome mantido por compatibilidade (Sellout destrutura `fetchOrdersAndStock`);
    // o prefetch de estoque que existia aqui era MORTO (`stockBySku` nunca era
    // lido por ninguem — doc D item 2 / FFINAL Onda 0) e foi removido.
    const fetchOrdersAndStock = useCallback(
        async ({ from, to, force = false, days = 15 } = {}) => {
            if (!isAuthenticated) {
                return null;
            }

            const now = new Date();
            const toDate = to ? new Date(to) : now;
            const fromDate = from ? new Date(from) : new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
            const requestedRange = normalizeRange(fromDate, toDate);
            const requestKey = `${requestedRange.from}|${requestedRange.to}`;

            // Usar refs para verificar cache sem criar dependencias
            if (!force && rangeCovers(ordersRangeRef.current, requestedRange)) {
                return { orders: ordersRef.current, range: ordersRangeRef.current };
            }

            if (inFlightKey.current === requestKey && inFlightPromise.current) {
                return inFlightPromise.current;
            }

            inFlightKey.current = requestKey;
            setOrdersLoading(true);
            setOrdersError(null);

            const doFetch = async () => {
                try {
                    // O range inteiro vinha numa resposta só: o servidor
                    // serializava dezenas de MB antes do primeiro byte e a aba
                    // ficava parada até o fim. Agora são páginas de vendas
                    // INTEIRAS, e a tela pinta a primeira enquanto o resto vem.
                    const fetchedOrders = [];
                    // Offset sobre vendas ordenadas por data desc: uma venda
                    // nova no meio da varredura empurra a lista e repete a
                    // borda. Contar a mesma venda duas vezes inflaria o
                    // faturamento — o Set é o que impede.
                    const seen = new Set();

                    // Sem range carimbado enquanto as páginas chegam: senão
                    // `rangeCovers` daria o parcial como snapshot completo.
                    setOrdersRange(null);
                    ordersRangeRef.current = null;

                    let hasMore = true;
                    for (let pagina = 0; hasMore; pagina += 1) {
                        if (pagina >= SELLOUT_MAX_PAGES) {
                            throw new Error(
                                `Período grande demais: mais de ${SELLOUT_MAX_PAGES * SELLOUT_PAGE_SIZE} pedidos. Reduza o intervalo de datas.`,
                            );
                        }
                        const response = await api.get(
                            `/sellout?date_from=${requestedRange.from}&date_to=${requestedRange.to}&limit=${SELLOUT_PAGE_SIZE}&offset=${pagina * SELLOUT_PAGE_SIZE}`,
                        );
                        for (const order of response?.results || []) {
                            const key = String(order?.order_id ?? order?.id ?? '');
                            if (key && seen.has(key)) {
                                continue;
                            }
                            if (key) {
                                seen.add(key);
                            }
                            fetchedOrders.push(order);
                        }

                        // Outra busca começou (o range mudou no meio): abandona
                        // esta, senão a resposta velha sobrescreveria a nova.
                        if (inFlightKey.current !== requestKey) {
                            return { orders: ordersRef.current, range: ordersRangeRef.current };
                        }

                        // Publica o parcial — a primeira página já dá o que
                        // renderizar. Cópia, e o ref aponta para a MESMA cópia:
                        // `fetchedOrders` ainda vai crescer.
                        const parcial = [...fetchedOrders];
                        setOrders(parcial);
                        ordersRef.current = parcial;

                        hasMore = Boolean(response?.paging?.has_more);
                    }

                    setOrders(fetchedOrders);
                    setOrdersRange(requestedRange);

                    // Atualizar refs
                    ordersRef.current = fetchedOrders;
                    ordersRangeRef.current = requestedRange;

                    return { orders: fetchedOrders, range: requestedRange };
                } catch (error) {
                    const message = error?.message || 'Falha ao buscar pedidos';
                    setOrdersError(message);
                    throw error;
                } finally {
                    // SÓ se ainda for a minha busca: com a paginação uma busca
                    // dura vários round-trips, e a que termina antes estaria
                    // apagando a guarda da outra e desligando o loading dela.
                    if (inFlightKey.current === requestKey) {
                        inFlightKey.current = null;
                        inFlightPromise.current = null;
                        setOrdersLoading(false);
                    }
                }
            };

            inFlightPromise.current = doFetch();
            return inFlightPromise.current;
        },
        [isAuthenticated],
    );

    // Atualizar refs quando o estado mudar
    useEffect(() => {
        ordersRef.current = orders;
    }, [orders]);

    useEffect(() => {
        ordersRangeRef.current = ordersRange;
    }, [ordersRange]);

    useEffect(() => {
        if (isAuthenticated) {
            return;
        }
        setOrders([]);
        setOrdersRange(null);
        setOrdersError(null);
        ordersRef.current = [];
        ordersRangeRef.current = null;
    }, [isAuthenticated]);

    const value = {
        orders,
        ordersRange,
        ordersLoading,
        ordersError,
        fetchOrdersAndStock,
    };

    return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
};

export const useDataCache = () => {
    const context = useContext(DataCacheContext);
    if (!context) {
        throw new Error('useDataCache deve ser usado dentro de DataCacheProvider');
    }
    return context;
};
